import { coreQueryKey } from '@/api/core/authorization';
import {
  readPropertyMediaGroups,
  readPropertyMediaItems,
  type MediaGroupsPage,
  type MediaItemsPage,
  type MediaTarget,
} from '@/api/core/property-media';
import { infiniteQueryOptions } from '@tanstack/react-query';
import { propertyRead, type PropertyRuntime } from '../property-queries';

/** 캐시 정리 목록(`api/query-client.ts`)에 같은 이름이 등록돼 있어야 한다. */
export const MEDIA_GROUPS_QUERY = 'property-media-groups';
export const MEDIA_ITEMS_QUERY = 'property-media-items';

const common = {
  // 서버 cursor는 대상·방향·내용 revision에 묶여 있어 오래 두면 409가 난다 — 들어올 때마다 첫 페이지부터
  // 새로 읽고, 화면을 떠나면 누적 목록을 버린다.
  staleTime: 0,
  gcTime: 0,
  retry: false as const,
  refetchOnWindowFocus: false as const,
  refetchOnReconnect: false as const,
  meta: { suppressGlobalError: true },
};

/**
 * 한 공간의 사진 그룹. 대상이 키에 들어가므로 바뀌면 다른 캐시 항목(처음 페이지)이 된다 —
 * 이전 대상의 cursor를 새 대상에 쓰는 일이 구조적으로 없다.
 */
export function propertyMediaGroupsQuery(
  runtime: PropertyRuntime,
  generation: number,
  propertyId: string,
  target: MediaTarget,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
  nativeCurrent = false,
) {
  return infiniteQueryOptions({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, MEDIA_GROUPS_QUERY, propertyId),
      target.kind,
      target.id,
      target.floor,
      ...(nativeCurrent ? ['native-current'] : []),
    ],
    queryFn: ({ pageParam, signal }): Promise<MediaGroupsPage> =>
      propertyRead(
        runtime,
        generation,
        () =>
          readPropertyMediaGroups(
            runtime.client,
            propertyId,
            target,
            { direction: 'asc', cursor: pageParam },
            signal,
            nativeCurrent,
          ),
        signal,
        isCurrent,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.page.nextCursor,
    enabled,
    ...common,
  });
}

/** 한 그룹의 항목. 저장된 순서(오름차순)로만 읽는다. */
export function propertyMediaItemsQuery(
  runtime: PropertyRuntime,
  generation: number,
  propertyId: string,
  groupId: string,
  enabled: boolean,
  isCurrent: () => boolean = () => true,
  nativeCurrent = false,
) {
  return infiniteQueryOptions({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, MEDIA_ITEMS_QUERY, propertyId),
      groupId,
      ...(nativeCurrent ? ['native-current'] : []),
    ],
    queryFn: ({ pageParam, signal }): Promise<MediaItemsPage> =>
      propertyRead(
        runtime,
        generation,
        () =>
          readPropertyMediaItems(
            runtime.client,
            propertyId,
            groupId,
            { direction: 'asc', cursor: pageParam },
            signal,
            nativeCurrent,
          ),
        signal,
        isCurrent,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.page.nextCursor,
    enabled,
    ...common,
  });
}
